import { Component, computed, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwButton } from '../../ui/button/uw-button';
import { UwBadge } from '../../ui/badge/uw-badge';
import { AiFeedbackService } from '../../../core/services/ai-feedback.service';
import { EvidenceStore } from '../../../core/stores/evidence.store';
import { AccountStore } from '../../../core/stores/account.store';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'uw-ai-feedback-popover',
  standalone: true,
  imports: [UwIcon, UwButton, UwBadge],
  template: `
    @if (feedback.selection(); as sel) {
      <div
        class="fixed z-popover w-[320px] max-w-[calc(100vw-24px)] rounded-xl border border-line bg-surface-elevated shadow-lg"
        [style.left.px]="left()"
        [style.top.px]="top()"
        role="dialog"
        aria-label="Feedback on Copilot output"
        (keydown.escape)="dismiss()"
      >
        <header class="flex items-center gap-2 border-b border-line px-3.5 py-2.5">
          <uw-icon name="sparkles" [size]="14" class="text-ai" />
          <span class="text-[12px] font-semibold text-primary">Help Copilot improve</span>
          <uw-badge tone="ai" subtle>RLHF</uw-badge>
          <button class="ml-auto rounded p-0.5 text-text-muted transition-colors hover:text-primary" (click)="dismiss()" aria-label="Dismiss feedback popover">
            <uw-icon name="x" [size]="14" />
          </button>
        </header>

        <div class="px-3.5 py-3">
          <p class="mb-2 line-clamp-2 rounded-md bg-surface-sunken px-2 py-1.5 text-[11.5px] italic leading-snug text-secondary">
            "{{ sel.text }}"
          </p>

          <div class="mb-2.5 grid grid-cols-2 gap-1.5">
            <button
              type="button"
              class="flex items-center justify-center gap-1.5 rounded-lg border py-1.5 text-[11.5px] font-semibold transition-colors"
              [class]="sentiment() === 'good' ? 'border-success bg-success-soft text-success' : 'border-line bg-surface text-secondary hover:border-success hover:text-success'"
              (click)="setSentiment('good')"
            >
              <uw-icon name="thumbs-up" [size]="13" />
              Good
            </button>
            <button
              type="button"
              class="flex items-center justify-center gap-1.5 rounded-lg border py-1.5 text-[11.5px] font-semibold transition-colors"
              [class]="sentiment() === 'fix' ? 'border-warning bg-warning-soft text-warning' : 'border-line bg-surface text-secondary hover:border-warning hover:text-warning'"
              (click)="setSentiment('fix')"
            >
              <uw-icon name="thumbs-down" [size]="13" />
              Needs fix
            </button>
          </div>

          @if (sentiment() === 'fix') {
            <textarea
              #correction
              rows="3"
              class="mb-2 w-full resize-none rounded-lg border border-line bg-surface px-2.5 py-2 text-[12px] text-primary placeholder:text-text-muted focus:border-ai focus:outline-none focus:ring-2 focus:ring-ai/20"
              placeholder="Describe the correction (e.g. the claim year is 2023, not 2024)…"
              [value]="correctionText()"
              (input)="correctionText.set(correction.value)"
            ></textarea>

            <label class="typo-label mb-1 block">Source of correction</label>
            <div class="relative">
              <select
                class="w-full cursor-pointer appearance-none rounded-lg border border-line bg-surface py-1.5 pl-2.5 pr-7 text-[11.5px] text-secondary focus:border-ai focus:outline-none"
                [value]="source()"
                (change)="source.set($any($event.target).value)"
              >
                <option value="narrative">Copilot narrative</option>
                @for (src of sources(); track src.id) {
                  <option [value]="src.id">{{ src.documentName }}</option>
                }
              </select>
              <uw-icon name="chevron-down" [size]="12" class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-text-muted" />
            </div>
          }
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-line bg-surface-sunken/50 px-3.5 py-2.5">
          <uw-button variant="ghost" size="sm" (click)="dismiss()">Skip</uw-button>
          <uw-button
            variant="ai"
            size="sm"
            [disabled]="sentiment() === 'fix' && !correctionText().trim()"
            [loading]="submitting()"
            (click)="submit()"
          >
            Submit feedback
          </uw-button>
        </footer>
      </div>
    }

    @if (submitted()) {
      <div class="fixed inset-0 z-modal flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Feedback submitted">
        <div class="absolute inset-0 bg-black/50" (click)="closeThanks()" aria-hidden="true"></div>
        <div class="relative w-full max-w-sm rounded-xl border border-line bg-surface-elevated p-6 text-center shadow-lg">
          <span class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-success">
            <uw-icon name="check" [size]="22" />
          </span>
          <h3 class="typo-title text-primary">Feedback recorded</h3>
          <p class="mt-1.5 text-[12px] leading-relaxed text-secondary">
            Thank you — your correction is logged against the source with full provenance and routed to the model team.
          </p>
          <uw-button variant="primary" class="mt-4" (click)="closeThanks()">Done</uw-button>
        </div>
      </div>
    }
  `,
})
export class UwAiFeedbackPopover {
  protected readonly feedback = inject(AiFeedbackService);
  private readonly evidence = inject(EvidenceStore);
  private readonly account = inject(AccountStore);
  private readonly toast = inject(ToastService);

  protected readonly sentiment = signal<'good' | 'fix' | null>(null);
  protected readonly correctionText = signal('');
  protected readonly source = signal('narrative');
  protected readonly submitting = signal(false);
  protected readonly submitted = signal(false);

  protected readonly sources = computed(() => this.evidence.sources());

  protected readonly left = computed(() => {
    const sel = this.feedback.selection();
    if (!sel) return 0;
    return Math.max(8, Math.min(sel.left, (typeof window !== 'undefined' ? window.innerWidth : 1024) - 328));
  });

  protected readonly top = computed(() => {
    const sel = this.feedback.selection();
    if (!sel) return 0;
    return Math.max(8, sel.top + sel.height + 10);
  });

  protected setSentiment(value: 'good' | 'fix'): void {
    this.sentiment.set(value);
    if (value === 'fix') {
      this.correctionText.set('');
    }
  }

  protected dismiss(): void {
    this.feedback.clearSelection();
    this.sentiment.set(null);
    this.correctionText.set('');
  }

  protected submit(): void {
    const sel = this.feedback.selection();
    if (!sel) return;
    this.submitting.set(true);
    this.feedback
      .submitFeedback({
        accountId: this.account.selectedAccountId(),
        selectedText: sel.text,
        correction: this.sentiment() === 'good' ? '' : this.correctionText(),
        source: this.sentiment() === 'good' ? 'narrative' : this.source(),
      })
      .pipe(take(1))
      .subscribe(() => {
        this.submitting.set(false);
        this.feedback.clearSelection();
        this.sentiment.set(null);
        this.correctionText.set('');
        this.submitted.set(true);
        this.toast.success('Feedback recorded', 'Your correction was routed to the Copilot team.');
      });
  }

  protected closeThanks(): void {
    this.submitted.set(false);
  }
}
